import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Clock, MapPin, Plus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import StatusBadge from "@/components/StatusBadge";
import Seo from "@/components/Seo";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports").then(({ data }) => setRecent(data.slice(0, 4))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const onSelect = (addr) => {
    sessionStorage.setItem("mtr_order_address", JSON.stringify(addr));
    sessionStorage.removeItem("mtr_order_product");
    navigate("/order/verify");
  };

  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <div className="container-mtr py-8 sm:py-12" data-testid="dashboard-page">
      <Seo title="Dashboard — MeasureTheRoof" description="Order a new roof measurement report." path="/dashboard" noindex />
      <div className="mb-8">
        <span className="eyebrow">Dashboard</span>
        <h1 className="font-body font-extrabold text-navy text-[26px] sm:text-[32px] mt-1">Hi {firstName} — let's measure a roof</h1>
      </div>

      <div className="card-mtr max-w-2xl">
        <h2 className="font-bold text-navy text-[18px] mb-1">Start a new report</h2>
        <p className="text-mortar text-[14px] mb-5">Enter a US property address. We'll confirm coverage before you pay.</p>
        <AddressAutocomplete onSelect={onSelect} autoFocus testid="dashboard-address" />
        <p className="text-[12px] text-mortar mt-3 flex items-center gap-1.5">
          <MapPin size={14} /> Tip: include the house number and ZIP for best results.
        </p>
      </div>

      <div className="mt-12">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-navy text-[20px] flex items-center gap-2"><Clock size={20} className="text-mortar" /> Recent reports</h2>
          <Link to="/reports" className="text-orange font-semibold text-[14px] flex items-center gap-1" data-testid="view-all-reports">
            View all <ArrowRight size={16} />
          </Link>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {[0, 1].map((i) => <div key={i} className="card-mtr h-28 skeleton rounded-card" />)}
          </div>
        ) : recent.length === 0 ? (
          <div className="card-mtr text-center py-10" data-testid="no-reports">
            <div className="w-12 h-12 rounded-full bg-fieldstone grid place-items-center mx-auto mb-3">
              <Plus className="text-orange" size={24} />
            </div>
            <p className="text-iron font-semibold">No reports yet</p>
            <p className="text-mortar text-[14px] mt-1">Enter an address above to order your first roof report.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {recent.map((r) => (
              <Link key={r.report_id} to={`/reports/${r.report_id}`} className="card-mtr hover:shadow-modal transition-shadow" data-testid={`recent-report-${r.report_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-iron text-[15px] leading-snug">{r.address?.full}</p>
                  <StatusBadge status={r.status} />
                </div>
                <div className="flex items-center justify-between mt-3 text-[13px] text-mortar">
                  <span>{r.product_name}</span>
                  <span className="metric text-iron">${Number(r.customer_price).toFixed(2)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
